/// <amd-module name="Analytics/Shared/TrendChart/Common/TrendChartHelper" />
import * as ChartSeries from "Analytics/Shared/ChartSeries/ChartSeries";
import { PassPercentSeries } from "Analytics/Shared/ChartSeries/PassPercentSeries";
import { Period, TrendBy } from "Analytics/Shared/Common/Types";
import { adjustDataForAllPeriod, IDataForDate } from "Analytics/Shared/Common/Utils/DateAdjuster";
import * as Resources from "Analytics/Shared/TrendChart/Common/Resources";
import { ITrendChartAggregateData, ITrendChartCustomData } from "Analytics/Shared/TrendChart/Common/Types";
import * as Utils_String from "VSS/Core/Util/String";
import { ChartTypesConstants, DataSeries, LabelFormatModes } from "VSS/Features/Charts/Contracts";

export class TrendChartHelper {
    public getCommonChartOptions(
        primaryChartData: { [key: string]: ITrendChartAggregateData[] },
        primaryChartSeries: ChartSeries.ChartSeries,
        secondaryAxisLabelFormatMode: LabelFormatModes,
        secondaryChartData?: ITrendChartAggregateData[],
        secondaryChartSeries?: ChartSeries.ChartSeries,
        startOnTick?: boolean,
        endOnTick?: boolean,
        customData?: any[],
        maxStackedSeriesCount = 10
    ): any {
        const dataSeries: DataSeries[] = [];
        let xAxisLabelValues: string[] = [];
        const chartTypes: string[] = [];
        const primaryChartSeriesLength = Object.keys(primaryChartData).length;

        if (primaryChartSeriesLength === 1 && primaryChartData[""]) {
            Object.keys(primaryChartData).forEach(stackBy => {
                const currChartData: { aggrValues: number[]; dates: string[] } = { aggrValues: [], dates: [] };
                primaryChartData[stackBy].forEach(d => (currChartData.aggrValues.push(d.aggrValue), currChartData.dates.push(d.date)));
                dataSeries.push({
                    name: primaryChartSeries.name,
                    color: primaryChartSeries.color,
                    data: currChartData.aggrValues,
                    customData: customData
                });
                xAxisLabelValues = currChartData.dates;
                chartTypes.push(ChartTypesConstants.StackedColumn);
            });
        } else {
            xAxisLabelValues = this.getStackedChartData(primaryChartData, customData, dataSeries, chartTypes, maxStackedSeriesCount);
        }
        if (secondaryChartSeries && secondaryChartSeries.values && secondaryChartSeries.values.length > 0) {
            dataSeries.push({
                name: secondaryChartSeries.name,
                color: secondaryChartSeries.color,
                useSecondaryAxis: true,
                data: secondaryChartSeries.values
            });
            chartTypes.push(ChartTypesConstants.Line);
            if (xAxisLabelValues.length == 0 && secondaryChartData) {
                xAxisLabelValues = secondaryChartData.map(d => d.date);
            }
        }

        const maxSecondaryYAxisLabel = this._getMaxSecondaryYAxisLabel(secondaryAxisLabelFormatMode, secondaryChartSeries);
        return {
            chartType: ChartTypesConstants.Hybrid, // For simple charts, we are looking for primary as column and secondary as line. This option takes care of both(even secondary not required) and hybrid option not required.
            specializedOptions: {
                chartTypes: chartTypes,
                includeMarkers: true
            },
            xAxis: {
                labelFormatMode: LabelFormatModes.Textual,
                labelsEnabled: true,
                labelValues: xAxisLabelValues // Here for both charts, we are choosing axis from primary chart. This shouldnt be a problem but need to analyze it.
            },
            yAxis: {
                allowDecimals: primaryChartSeries.allowDecimal,
                labelFormatMode: LabelFormatModes.Linear,
                labelsEnabled: true,
                markingsEnabled: false,
                title: primaryChartSeries.name,
                min: primaryChartSeries.minValue,
                max: primaryChartSeries.maxValue
            },
            yAxisSecondary: secondaryChartData
                ? {
                      allowDecimals: secondaryChartSeries ? secondaryChartSeries.allowDecimal : false,
                      labelFormatMode: secondaryAxisLabelFormatMode,
                      labelsEnabled: true,
                      markingsEnabled: false,
                      startOnTick: startOnTick,
                      endOnTick: maxSecondaryYAxisLabel ? false : endOnTick,
                      title: secondaryChartSeries ? secondaryChartSeries.name : "name",
                      max: maxSecondaryYAxisLabel
                  }
                : null,
            series: dataSeries
        };
    }

    public adjustDataForAllStackBySeries(
        stackByToAggrMap: { [key: string]: ITrendChartAggregateData[] },
        trendBy?: TrendBy,
        period?: Period
    ): { [key: string]: ITrendChartAggregateData[] } {
        if (!stackByToAggrMap) {
            return stackByToAggrMap;
        }

        Object.keys(stackByToAggrMap).forEach(stackBy => {
            stackByToAggrMap[stackBy] = this._adjustDataForAllPeriod(stackByToAggrMap[stackBy], trendBy, period);
        });

        return stackByToAggrMap;
    }

    public getStackedChartData(
        primaryChartData: { [key: string]: ITrendChartAggregateData[] },
        customData: any[] | undefined,
        dataSeries: DataSeries[],
        chartTypes: string[],
        maxStackedSeriesCount: number,
        customColors?: string[]
    ) {
        let xAxisLabelValues: string[] = [];
        const stackByToTotalAggrValue: { stackBy: string; value: number }[] = [];
        const formattedPrimaryChartData: {
            [key: string]: { aggrValues: number[]; accumulatedValue: number; dates: string[] };
        } = {};
        Object.keys(primaryChartData).forEach(stackBy => {
            formattedPrimaryChartData[stackBy] = { aggrValues: [], accumulatedValue: 0, dates: [] };
            primaryChartData[stackBy].sort((aggrData1, aggrData2) => Utils_String.localeComparer(aggrData1.date, aggrData2.date));
            primaryChartData[stackBy].forEach(
                d => (
                    formattedPrimaryChartData[stackBy].aggrValues.push(d.aggrValue),
                    formattedPrimaryChartData[stackBy].dates.push(d.date),
                    (formattedPrimaryChartData[stackBy].accumulatedValue += d.aggrValue)
                )
            );
            stackByToTotalAggrValue.push({
                stackBy: stackBy,
                value: formattedPrimaryChartData[stackBy].accumulatedValue
            });
            // Getting any stackValue's data here
            if (xAxisLabelValues.length === 0) {
                xAxisLabelValues = formattedPrimaryChartData[stackBy].dates;
            }
        });

        this._reduceAndCombineStackedChartSeries(
            formattedPrimaryChartData,
            stackByToTotalAggrValue,
            xAxisLabelValues.length,
            customData,
            dataSeries,
            chartTypes,
            maxStackedSeriesCount,
            customColors
        );
        return xAxisLabelValues;
    }

    private _getMaxSecondaryYAxisLabel(
        secondaryAxisLabelFormatMode: LabelFormatModes,
        secondaryChartSeries?: ChartSeries.ChartSeries
    ): number | undefined {
        if (secondaryAxisLabelFormatMode !== LabelFormatModes.Percentage || !secondaryChartSeries) {
            return undefined;
        }

        const seriesValues = secondaryChartSeries.values.filter((value: number) => {
            return value !== null && value !== undefined;
        });

        const maxSeriesValue = Math.max(...seriesValues);
        // Limit max secondary y axis label to 100, if data set is greater than 80.
        return maxSeriesValue >= 80 ? (secondaryChartSeries as PassPercentSeries).maxValue : undefined;
    }

    private _adjustDataForAllPeriod(data: ITrendChartAggregateData[], trendBy?: TrendBy, period?: Period): ITrendChartAggregateData[] {
        if (!data || data.length <= 0) {
            return data;
        }

        let adjustableData = data.map(datum => {
            return { date: datum.date, data: datum.aggrValue, customData: datum.customData } as IDataForDate<number>;
        });

        adjustableData = adjustDataForAllPeriod(adjustableData, trendBy, period, null);

        const adjustedData = adjustableData.map(datum => {
            return { date: datum.date, aggrValue: datum.data, customData: datum.customData as ITrendChartCustomData } as ITrendChartAggregateData;
        });

        return adjustedData;
    }

    private _reduceAndCombineStackedChartSeries(
        formattedPrimaryChartData: { [key: string]: { aggrValues: number[]; dates: string[] } },
        stackByTotalAggrValue: { stackBy: string; value: number }[],
        XValueLength: number,
        customData: any[] | undefined,
        dataSeries: DataSeries[],
        chartTypes: string[],
        maxStackedSeriesCount: number,
        customColors?: string[] | undefined
    ) {
        stackByTotalAggrValue.sort((aggr1, aggr2) => {
            return aggr2.value - aggr1.value;
        });

        // Populate top (n) stacked data.
        let count = 0;
        const len = stackByTotalAggrValue.length;
        for (; count < len && count < maxStackedSeriesCount; count++) {
            const stackBy: string = stackByTotalAggrValue[count].stackBy;
            if(customColors) {
                dataSeries.push({
                    name: stackBy,
                    data: formattedPrimaryChartData[stackBy].aggrValues,
                    customData: customData,
                    color: customColors[count]
                });
            } else {
                dataSeries.push({
                    name: stackBy,
                    data: formattedPrimaryChartData[stackBy].aggrValues,
                    customData: customData,
                });
            }
            delete formattedPrimaryChartData[stackBy];
            chartTypes.push(ChartTypesConstants.StackedColumn);
        }

        if (count < stackByTotalAggrValue.length) {
            // Populate remaining chart data as Others in new chart data.
            const remainingStackDateToAggrData: number[] = new Array(XValueLength).fill(0);
            let otherStackedDataCount = 0;

            Object.keys(formattedPrimaryChartData).forEach((stackBy: string) => {
                // Populate stack data if not populated in filtered list.
                otherStackedDataCount++;
                for (let index = 0; index < formattedPrimaryChartData[stackBy].dates.length; index++) {
                    remainingStackDateToAggrData[index] += formattedPrimaryChartData[stackBy].aggrValues[index] || 0;
                }
            });
            dataSeries.push({
                name: Utils_String.format(Resources.OthersArtifactWithCountText, otherStackedDataCount),
                data: remainingStackDateToAggrData,
                customData: customData
            });
            chartTypes.push(ChartTypesConstants.StackedColumn);
        }
    }
}
